import {
  AccessCredential,
  RefreshCredential,
  WorkspaceContext,
  WorkspaceContextType,
} from '../types/authTypes';

const WORKSPACE_CONTEXT_KEY = 'tuos.workspace_context';
const WORKSPACE_PREFERENCE_KEY = 'tuos.last_workspace';

export interface WorkspacePreference {
  membershipId: string;
  tenantId: string;
  tenantName?: string;
  companyId?: string;
  companyName?: string;
  contextType: WorkspaceContextType;
}

let _accessCredential: AccessCredential | null = null;
let _refreshCredential: RefreshCredential | null = null;
let _workspaceContext: WorkspaceContext | null = null;

export function setAccessCredential(cred: AccessCredential): void {
  _accessCredential = cred;
}

export function getAccessCredential(): AccessCredential | null {
  return _accessCredential;
}

export function setRefreshCredential(cred: RefreshCredential): void {
  _refreshCredential = cred;
}

export function getRefreshCredential(): RefreshCredential | null {
  return _refreshCredential;
}

export function setWorkspacePreference(ctx: WorkspaceContext): void {
  try {
    // Preserve existing names when the incoming context lacks them (e.g. restored
    // from a JWT that never carries tenant_name / company_name claims).
    const existing = getWorkspacePreference();
    const isSameMembership = existing?.membershipId === ctx.membershipId;
    const pref: WorkspacePreference = {
      membershipId: ctx.membershipId,
      tenantId: ctx.tenantId,
      tenantName:
        ctx.tenantName ?? (isSameMembership ? existing?.tenantName : undefined),
      companyId: ctx.companyId,
      companyName:
        ctx.companyName ??
        (isSameMembership ? existing?.companyName : undefined),
      contextType: ctx.contextType,
    };
    localStorage.setItem(WORKSPACE_PREFERENCE_KEY, JSON.stringify(pref));
  } catch {
    // localStorage unavailable
  }
}

export function getWorkspacePreference(): WorkspacePreference | null {
  try {
    const raw = localStorage.getItem(WORKSPACE_PREFERENCE_KEY);
    if (raw) return JSON.parse(raw) as WorkspacePreference;
  } catch {
    // ignore
  }
  return null;
}

export function clearWorkspacePreference(): void {
  try {
    localStorage.removeItem(WORKSPACE_PREFERENCE_KEY);
  } catch {
    // ignore
  }
}

export function setWorkspaceContext(ctx: WorkspaceContext): void {
  _workspaceContext = ctx;
  setWorkspacePreference(ctx);
  try {
    sessionStorage.setItem(WORKSPACE_CONTEXT_KEY, JSON.stringify(ctx));
  } catch {
    // sessionStorage unavailable — in-memory only
  }
}

export function getWorkspaceContext(): WorkspaceContext | null {
  if (_workspaceContext) return _workspaceContext;
  // Recover from sessionStorage after page refresh
  try {
    const raw = sessionStorage.getItem(WORKSPACE_CONTEXT_KEY);
    if (raw) {
      _workspaceContext = JSON.parse(raw) as WorkspaceContext;
      return _workspaceContext;
    }
  } catch {
    // ignore
  }
  return null;
}

export function clearCredentials(): void {
  _accessCredential = null;
  _refreshCredential = null;
  _workspaceContext = null;
  clearWorkspacePreference();
  try {
    sessionStorage.removeItem(WORKSPACE_CONTEXT_KEY);
  } catch {
    // ignore
  }
}

export function hasWorkspaceContext(): boolean {
  return !!_accessCredential?.tenantId;
}
